import React, {useEffect, useState, useRef} from 'react';
import {GetStaticProps} from 'next';
import {Box} from '@chakra-ui/react';
import {ResultOf} from '@graphql-typed-document-node/core';
import {TitleMetaLinkTag} from 'react-datocms';
import {useUserQuery} from '../graphql/generated';
import {
  ProductPageDocument,
  ProductModelHeroTitleField,
  ProductModelHeroContentField,
  StaticGenerationProductPagesDocument,
} from '../graphql/datocms-generated';
import {request} from '../graphql/api';
import {useCurrentAssetOverview} from '../hooks/current-asset-overview';
import {PageLayout} from '../components/global/page-layout';
import {ProductHero} from '../components/product/product-hero';
import {getCmsTheme} from '../utils/utils';
import {ProductMobileNavBar} from '../components/global/product-mobile-nav-bar';
import {NavBar} from '../components/global/nav-bar';
import {ScrollProvider, useScrollContext} from '../hooks/scrollable-context';
import {ProductComponentLoader} from '../components/sections/loaders/product-component-loader';
import {ProductFooterLoader} from '../components/product/product-footer-loader';

interface StoryPageTemplateProps {
  pageData: ResultOf<typeof ProductPageDocument>['product'];
}

function StoryLayout(props: {children?: React.ReactNode | React.ReactNode[]}) {
  return (
    <ScrollProvider sections={{}} defaultSection="painting">
      {/* <NavigationOverlay sections={SECTIONS} hideNavbar hideFooterBar> */}
      {props.children}
      {/* </NavigationOverlay> */}
    </ScrollProvider>
  );
}

const StoryPageTemplate = ({pageData}: StoryPageTemplateProps): JSX.Element => {
  const userQuery = useUserQuery();
  const {scrollY} = useScrollContext();
  const asset = useCurrentAssetOverview(pageData?.asset?.assetId || '');
  const [direction, setDirection] = useState<'down' | 'up'>();
  const refToThePrevScrollValue = useRef(80);
  const themeColors = getCmsTheme(pageData?.asset?.theme || '');

  const productSlug = pageData?.asset?.product
    ? pageData?.asset?.product.slug
    : pageData?.asset?.slug;

  useEffect(() => {
    if (typeof scrollY === 'number') {
      if (scrollY <= 80) {
        setDirection('up');
      }
      if (refToThePrevScrollValue.current < scrollY) {
        setDirection('down');
      }
      if (refToThePrevScrollValue.current > scrollY) {
        setDirection('up');
      }
      refToThePrevScrollValue.current = scrollY;
    }
  }, [scrollY]);

  return (
    <PageLayout
      firstName={userQuery.data?.user?.firstName}
      hideShapes
      seo={pageData?.seo as TitleMetaLinkTag[]}
      additionalContainerStyles={{
        pb: {base: '145px', md: '80px'},
      }}
      additionalContentStyles={{
        pt: 0,
        pb: 0,
        overflowX: 'hidden',
      }}
      CustomNavbar={
        <>
          <Box display={{base: 'block', lg: 'none'}}>
            <ProductMobileNavBar
              username={userQuery.data?.user?.firstName}
              background={themeColors?.primary}
              headerBaseText={pageData?.title || ''}
              accent={themeColors?.secondary || ''}
              variant="dark"
              productLink={productSlug || ''}
              purchaseLink={`own-a-piece/${pageData?.asset?.slug}`}
              activeLink={productSlug || ''}
              hideSubNav={asset.status === 'COMING_SOON'}
            />
          </Box>
          <Box display={{base: 'none', lg: 'flex'}}>
            <NavBar
              username={userQuery.data?.user?.firstName}
              position={direction}
            />
          </Box>
        </>
      }
      FooterBanner={
        <>
          <ProductFooterLoader
            assetId={pageData?.asset?.assetId || ''}
            color={themeColors ? themeColors.secondary : 'stampRed'}
            purchaseUrl={`own-a-piece/${pageData?.asset?.slug}`}
            price={asset.priceString}
          />
        </>
      }
    >
      <ProductHero
        status={asset?.status || ''}
        bgColor={themeColors ? themeColors.primary : 'midnight'}
        accentColor={themeColors ? themeColors.secondary : 'stampRed'}
        image={pageData?.heroImage?.responsiveImage || undefined}
        title={pageData?.heroTitle as ProductModelHeroTitleField}
        body={pageData?.heroContent as ProductModelHeroContentField}
        price={asset.priceString}
        pieces={asset.totalAssetPieces?.toString()}
        setBackgroundImage={pageData?.heroBackgroundImage || false}
        desktopBackgroundImage={
          pageData?.heroDesktopImage?.responsiveImage || undefined
        }
        mobileBackgroundImage={
          pageData?.heroMobileImage?.responsiveImage || undefined
        }
        partnershipLogo={pageData?.heroPartnershipLogo?.url || undefined}
      />

      {pageData?.sections &&
        pageData.sections.map((section: any) => (
          <React.Fragment key={section.id}>
            <ProductComponentLoader
              // eslint-disable-next-line no-underscore-dangle
              component={section._modelApiKey}
              section={section}
              primaryColor={themeColors ? themeColors.primary : 'midnight'}
              secondaryColor={themeColors ? themeColors.secondary : 'stampRed'}
            />
          </React.Fragment>
        ))}
    </PageLayout>
  );
};

StoryPageTemplate.getLayout = function getLayout(page: React.ReactElement) {
  return <StoryLayout>{page}</StoryLayout>;
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export async function getStaticPaths() {
  const prodtuctData = await request(StaticGenerationProductPagesDocument);

  return {
    fallback: 'blocking',
    paths: prodtuctData.allProducts.map((product) => `/${product.slug}`),
  };
}

export const getStaticProps: GetStaticProps = async (props) => {
  const pageData = await request(ProductPageDocument, {
    slug: props.params?.product as string,
  });

  return {
    revalidate: 10,
    notFound: !pageData.product,
    props: {
      pageData: pageData.product,
    },
  };
};

export default StoryPageTemplate;
